import {
  iprLLToothData,
  iprLRToothData,
  iprULToothData,
  iprURToothData,
} from "@/shared/data/IPR-Report-v2"
import {
  EIPRToothMap,
  IIPRDataItem,
  IIPRLogItem,
  IIPRToothData,
} from "@/shared/types"
import classNames from "classnames"
import {
  FC,
  RefObject,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react"
import { toothIdTypes } from "../column"
import StageItemValue from "./StageItemValue"

import { useAppDispatch, useAppSelector } from "@/core/app/hooks"
import { RootState } from "@/core/app/store"
import { caseManagement, EArchType, iprModule } from "@/gluelayer"
import { setNotes } from "@/UDTreat/udTreatSlice"
import AddIcon from "@mui/icons-material/Add"
import CloseIcon from "@mui/icons-material/Close"
import DeleteForeverIcon from "@mui/icons-material/DeleteForever"
import EditIcon from "@mui/icons-material/Edit"
import {
  Alert,
  Button,
  CircularProgress,
  IconButton,
  Snackbar,
  TextField,
  Tooltip,
} from "@mui/material"
import { useParams } from "react-router-dom"
import MyLowPolyline from "./MyLowPolyline"
import MyStageInput from "./MyStageInput"
import MyUpPolyline from "./MyUpPolyline"

import MyDialog from "@/shared/components/MyDialog"
import "./scss/index.scss"

export interface IUpdateToothDataParams {
  dataType:
    | "iprURToothDataMemo"
    | "iprULToothDataMemo"
    | "iprLRToothDataMemo"
    | "iprLLToothDataMemo"
  id: EIPRToothMap
  itemIndex: number
  value: IIPRLogItem
}

interface IEditStageItem {
  type: EArchType | null
  editIndex: number
}
interface IProps {
  toothlist: number[]
  iprPlanData: Record<number, IIPRDataItem[]>
  notes: string
  isPDF?: boolean
  onRef?: RefObject<{ onSubmit: () => Promise<boolean> }>
}

export interface IToothIPRData {
  iprURToothDataMemo: IIPRToothData[]
  iprULToothDataMemo: IIPRToothData[]
  iprLRToothDataMemo: IIPRToothData[]
  iprLLToothDataMemo: IIPRToothData[]
}
const IPRReport: FC<IProps> = ({
  toothlist,
  iprPlanData,
  isPDF,
  onRef,
  notes,
}) => {
  const dispatch = useAppDispatch()
  const { patientId, caseId } = useParams()
  const { switchViewEdit } = useAppSelector(
    (state: RootState) => state.udTreatService,
  )

  const myTextAreaRef = useRef<any>()
  const [notesValue, setNotesValue] = useState<string>(notes)
  const [message, setMessage] = useState<string>("")
  const [editStageItem, setEditStageItem] = useState<IEditStageItem>({
    type: null,
    editIndex: -1,
  })
  const [isDeleteStage, setIsDeleteStage] = useState<boolean>(false)
  const [deleteStageItem, setDeleteStageItem] = useState<IEditStageItem>({
    type: null,
    editIndex: -1,
  })
  const [toothIPRData, setToothIPRData] = useState<IToothIPRData>({
    iprURToothDataMemo: [],
    iprULToothDataMemo: [],
    iprLRToothDataMemo: [],
    iprLLToothDataMemo: [],
  })

  const iprData = useMemo(() => {
    const newIPRData: Record<number, IIPRLogItem[]> = {}
    const planIprKeys = Object.keys(iprPlanData)
    const iprLog = caseManagement.getActualIPRValue()
    const logIprKeys = Object.keys(iprLog)
    // console.log("-=-=-=-=-=iprPlanData", iprPlanData)
    // console.log("-=-=-=-=-=iprLog", iprLog)
    if (!logIprKeys.length) {
      for (const key of planIprKeys) {
        newIPRData[key] = iprPlanData[key].map((item) => ({
          ...item,
          actualIPRValue: "",
          isActive: 0, // 0: not active, 1: active
        }))
      }
    } else {
      // 合并 iprPlanData 和 iprLog 的数据
      const allKeys = [...new Set([...planIprKeys, ...logIprKeys])]

      for (const key of allKeys) {
        const planItems: IIPRDataItem[] = iprPlanData[key] || []
        const logItems: IIPRLogItem[] = iprLog[key] || []

        // 收集所有唯一的 stage
        const allStages = new Set<number | string>([])
        planItems.forEach((item) => allStages.add(item.stage))
        logItems.forEach((item) => allStages.add(item.stage))

        // 创建 map 以 stage 为 key，方便查找
        const planMap = new Map(planItems.map((item) => [item.stage, item]))
        const logMap = new Map(logItems.map((item) => [item.stage, item]))

        // 遍历所有 stage，合并数据
        newIPRData[key] = Array.from(allStages)
          .map((stage) => {
            const planItem = planMap.get(stage)
            const logItem = logMap.get(stage)

            // 如果 log 中有对应 stage 的数据，则合并
            if (logItem) {
              return {
                stage,
                value: planItem?.value || "",
                actualIPRValue: logItem.actualIPRValue || "",
                isActive: logItem.isActive || 0,
              }
            }

            // 如果只有 plan 数据
            if (planItem) {
              return {
                ...planItem,
                actualIPRValue: "",
                isActive: 0,
              }
            }

            // 如果只有 log 数据（理论上不应该出现，但为了健壮性）
            return {
              stage,
              value: "",
              actualIPRValue: logItem?.actualIPRValue || "",
              isActive: logItem?.isActive || 0,
            }
          })
          .sort((a, b) => Number(a.stage) - Number(b.stage))
      }
    }
    return newIPRData
  }, [iprPlanData])

  /**
   * Utils Function
   */
  const isEmpty = (value: string | number): boolean => {
    if (typeof value === "string") return value.trim() === ""
    if (typeof value === "number") return false
    return true
  }

  /**
   * Get Slice Tooth Id Types
   */
  const getSliceToothIdTypes = (start: number, end: number) => {
    const Types = Object.values(toothIdTypes)
    return Types.slice(start, end)
  }

  /**
   * Get Stage List
   */
  const getStageList = (type: "up" | "low") => {
    if (!iprData || !Object.values(iprData).length) {
      return []
    }
    const iprValuesObj =
      type === "up"
        ? Object.values(iprData).slice(0, 16)
        : Object.values(iprData).slice(16, 32)

    const iprValues = iprValuesObj.flatMap((item) => item)
    const stageValues = iprValues.map((item) => item.stage)
    const uniqueStageValues = [...new Set(stageValues)].sort((a, b) => a - b)
    return uniqueStageValues
  }

  const getIprURToothData = () => {
    const IPRData = [...iprURToothData].reverse()
    const types = getSliceToothIdTypes(0, 8)
    const stageList = getStageList("up")
    const mapData = IPRData.map((item, index) => {
      let itemData = (iprData[index + 1] || []).map((i) => ({
        ...i,
        actualIPRValue:
          i.actualIPRValue && Number(i.actualIPRValue) !== 0
            ? Number(i.actualIPRValue).toFixed(1)
            : "",
        isActive: i.isActive || 0,
      }))
      if (stageList.length) {
        stageList.forEach((stage) => {
          const isAdd = !itemData.some((i) => i.stage === stage)
          if (isAdd) {
            itemData.push({ stage, value: "", actualIPRValue: "", isActive: 0 })
          }
        })
      }

      return {
        ...item,
        isMissingTooth:
          !types[index].some((i) => toothlist.includes(i)) || index === 0,
        //@ts-ignore
        data: itemData.sort((a, b) => b.stage - a.stage),
      }
    })
    return mapData
  }

  const getIprULToothData = () => {
    const IPRData = [...iprULToothData]
    const types = getSliceToothIdTypes(8, 16)
    const stageList = getStageList("up")
    const mapData = IPRData.map((item, index) => {
      let itemData = (iprData[index + 9] || []).map((i) => ({
        ...i,
        actualIPRValue:
          i.actualIPRValue && Number(i.actualIPRValue) !== 0
            ? Number(i.actualIPRValue).toFixed(1)
            : "",
        isActive: i.isActive || 0,
      }))
      if (stageList.length) {
        stageList.forEach((stage) => {
          const isAdd = !itemData.some((i) => i.stage === stage)
          if (isAdd) {
            itemData.push({ stage, value: "", actualIPRValue: "", isActive: 0 })
          }
        })
      }
      return {
        ...item,
        isMissingTooth:
          !types[index].some((i) => toothlist.includes(i)) ||
          index === IPRData.length - 1,

        //@ts-ignore
        data: itemData.sort((a, b) => b.stage - a.stage),
      }
    })
    return mapData
  }
  const getIprLLToothData = () => {
    const IPRData = [...iprLLToothData]

    const types = getSliceToothIdTypes(16, 24)
    const stageList = getStageList("low")
    const mapData = IPRData.map((item, index) => {
      let itemData = (iprData[24 - index] || []).map((i) => ({
        ...i,
        actualIPRValue:
          i.actualIPRValue && Number(i.actualIPRValue) !== 0
            ? Number(i.actualIPRValue).toFixed(1)
            : "",
        isActive: i.isActive || 0,
      }))

      if (stageList.length) {
        stageList.forEach((stage) => {
          const isAdd = !itemData.some((i) => i.stage === stage)
          if (isAdd) {
            itemData.push({ stage, value: "", actualIPRValue: "", isActive: 0 })
          }
        })
      }
      return {
        ...item,
        isMissingTooth:
          !types[7 - index].some((i) => toothlist.includes(i)) ||
          index === IPRData.length - 1,

        //@ts-ignore
        data: itemData.sort((a, b) => a.stage - b.stage),
      }
    })
    return mapData
  }

  const getIprLRToothData = () => {
    const IPRData = [...iprLRToothData].reverse()
    const types = getSliceToothIdTypes(24, 32)
    const stageList = getStageList("low")
    const mapData = IPRData.map((item, index) => {
      let itemData = (iprData[32 - index] || []).map((i) => ({
        ...i,
        actualIPRValue:
          i.actualIPRValue && Number(i.actualIPRValue) !== 0
            ? Number(i.actualIPRValue).toFixed(1)
            : "",
        isActive: i.isActive || 0,
      }))

      if (stageList.length) {
        stageList.forEach((stage) => {
          const isAdd = !itemData.some((i) => i.stage === stage)
          if (isAdd) {
            itemData.push({ stage, value: "", actualIPRValue: "", isActive: 0 })
          }
        })
      }
      return {
        ...item,
        isMissingTooth:
          !types[7 - index].some((i) => toothlist.includes(i)) || index === 0,
        //@ts-ignore
        data: itemData.sort((a, b) => a.stage - b.stage),
      }
    })

    return mapData
  }

  useEffect(() => {
    const iprURToothDataMemo = getIprURToothData()
    const iprULToothDataMemo = getIprULToothData()
    const iprLRToothDataMemo = getIprLRToothData()
    const iprLLToothDataMemo = getIprLLToothData()
    setToothIPRData({
      iprURToothDataMemo,
      iprULToothDataMemo,
      iprLRToothDataMemo,
      iprLLToothDataMemo,
    })
  }, [iprData])

  /**
   * Upper Stage Value
   */
  const upperStageValue = useMemo(() => {
    const upData = [
      ...toothIPRData.iprURToothDataMemo,
      ...toothIPRData.iprULToothDataMemo,
    ]
    const stageValues = upData.map((item) => item.data).flatMap((item) => item)
    const plannedValues =
      stageValues.reduce((acc, cur) => {
        return acc + Number(cur.value)
      }, 0) || 0
    const actuallyPlacedValues =
      stageValues.reduce((acc, cur) => {
        const newCur = cur.isActive ? cur.value : cur.actualIPRValue
        return acc + Number(newCur)
      }, 0) || 0

    return {
      plannedValues: plannedValues.toFixed(1),
      actuallyPlacedValues: actuallyPlacedValues.toFixed(1),
    }
  }, [toothIPRData.iprURToothDataMemo, toothIPRData.iprULToothDataMemo])

  /**
   * lower Stage Value
   */
  const lowerStageValue = useMemo(() => {
    const upData = [
      ...toothIPRData.iprLRToothDataMemo,
      ...toothIPRData.iprLLToothDataMemo,
    ]
    const stageValues = upData.map((item) => item.data).flatMap((item) => item)
    const plannedValues =
      stageValues.reduce((acc, cur) => {
        return acc + Number(cur.value)
      }, 0) || 0
    const actuallyPlacedValues =
      stageValues.reduce((acc, cur) => {
        const newCur = cur.isActive ? cur.value : cur.actualIPRValue
        return acc + Number(newCur)
      }, 0) || 0

    return {
      plannedValues: plannedValues.toFixed(1),
      actuallyPlacedValues: actuallyPlacedValues.toFixed(1),
    }
  }, [toothIPRData.iprLRToothDataMemo, toothIPRData.iprLLToothDataMemo])

  /**
   * Update Tooth Data
   */
  const updateToothData = (data: IUpdateToothDataParams) => {
    const newData = toothIPRData[data.dataType].map((item) => {
      if (item.id === data.id) {
        return {
          ...item,
          data: item.data.map((i, index) => {
            if (index === data.itemIndex) {
              return {
                ...i,
                ...data.value,
              }
            }
            return i
          }),
        }
      }
      return item
    })
    setToothIPRData({
      ...toothIPRData,
      [data.dataType]: newData,
    })
  }

  /**
   * Upload
   */
  // const upload = async () => {
  //   try {
  //     if (!wasmModule.isFinishedOpencase) return
  //     const res = await caseManagement.getCaseFiles(["bulk2"])
  //     const zips = []
  //     if (res) {
  //       for (const k in res) {
  //         zips.push({
  //           fileName: k,
  //           file: res[k],
  //         })
  //       }

  //       await dispatch(
  //         uploadCaseZips({
  //           zips,
  //           patientId,
  //           caseId,
  //         }),
  //       )
  //       dispatch(setNotes(""))
  //       return Promise.resolve(true)
  //     }
  //   } catch (e) {
  //     Promise.reject(false)
  //     console.log("upload error", e)
  //   }
  // }

  // const upState = () => {
  //   const { attachment, toothlist, ipr } =
  //     caseManagement.getAttachmentIPRReport(true)
  //   const { lowlist, uplist } = toothlist
  //   const list = lowlist.concat(uplist)
  //   dispatch(setNotes(notesValue))
  //   dispatch(
  //     setReportData({
  //       attachment: attachment,
  //       toothlist: list,
  //       iprData: ipr,
  //     }),
  //   )
  // }

  const onSubmit = async () => {
    let objData = {}

    const toothlist1 = toothIPRData.iprURToothDataMemo.map((item) => item.data)
    const toothlist2 = toothIPRData.iprULToothDataMemo.map((item) => item.data)
    const toothlist3 = toothIPRData.iprLRToothDataMemo
      .map((item) => item.data)
      .reverse()
    const toothlist4 = toothIPRData.iprLLToothDataMemo
      .map((item) => item.data)
      .reverse()

    const toothlist = [
      ...toothlist1,
      ...toothlist2,
      ...toothlist4,
      ...toothlist3,
    ]
    const toothlistMap = toothlist.map((item) => {
      const toothItem = item
        .map((i) => {
          const newItem = {
            ...i,
            value: Number(i.value) ? Number(i.value).toFixed(1) : "0",
            actualIPRValue: Number(i.actualIPRValue)
              ? Number(i.actualIPRValue).toFixed(1)
              : "0",
            isActive: i.isActive || 0,
          }
          return newItem
        })
        .filter((item) => item.stage !== null)

      return toothItem
    })
    toothlistMap.forEach((item, index) => {
      objData[index + 1] = item
    })

    const jsonData = JSON.stringify(objData)
    // console.log("==========jsonData", jsonData)
    const res = caseManagement.setActualIPRValue(jsonData)
    const resMessage = iprModule.setIPRMessage(notesValue)
    if (res || resMessage) {
      dispatch(setNotes(notesValue))
      // upState()
      // const isUploadSuccess = await upload()
      // if (isUploadSuccess) {
      //   upState()
      // }
      return Promise.resolve(true)
    } else {
      return Promise.resolve(true)
    }
  }

  /**
   * TextArea onKeyDown
   */
  const onKeyDown = async (e) => {
    if (e.keyCode === 27 || e.keyCode === 13) {
      const val = e.target.value
      dispatch(setNotes(val))
      const res = iprModule.setIPRMessage(val)

      if (res) {
        myTextAreaRef.current?.blur()
        // await upload()
      }
    }
  }

  /**
   *Check if there is a non-missing tooth after the specified index.
   */
  const hasNonMissingToothAfter = (
    dataList: IIPRToothData[],
    currentIndex: number,
  ): boolean => {
    for (let i = currentIndex + 1; i < dataList.length; i++) {
      if (!dataList[i].isMissingTooth) {
        return true
      }
    }
    return false
  }
  const hasNonMissingToothBefore = (
    dataList: IIPRToothData[],
    currentIndex: number,
  ): boolean => {
    for (let i = currentIndex - 1; i >= 0; i--) {
      if (!dataList[i].isMissingTooth) {
        return true
      }
    }
    return false
  }

  /**
   * Update Stage Data
   */

  const updateStageData = (
    val: string | number,
    type: EArchType,
    stageIndex: number,
  ) => {
    const min = 0
    const max = 100
    const newData = {}
    const types =
      type === EArchType.UpArch
        ? ["iprURToothDataMemo", "iprULToothDataMemo"]
        : ["iprLRToothDataMemo", "iprLLToothDataMemo"]

    const currentStage = toothIPRData[types[0]][0].data.map(
      (item) => item.stage,
    )
    let value = val

    // Check if it is empty
    if (value === "") {
      return
    }
    value = Number(value)
    //Check whether it exceeds the range
    if (value < min || value > max) {
      setMessage("Please enter the correct Stage")
      return
    }
    //Check whether there is an identical stage
    const isSameStage = currentStage.includes(Number(value))
    if (isSameStage) {
      setMessage("Stage already exists")
      return
    }
    //Delete
    if (editStageItem.type !== null && editStageItem.editIndex !== -1) {
      iprModule.clearActualIPRValueByStepIndex(
        toothIPRData[types[0]][0].data[stageIndex].stage,
      )
    }

    types.forEach((item) => {
      newData[item] = toothIPRData[item].map((i) => {
        return {
          ...i,
          data: i.data
            .map((j, dIndex) => {
              if (dIndex === stageIndex) {
                return {
                  ...j,
                  stage: Number(value),
                }
              }
              return j
            })
            .sort((a, b) =>
              type === EArchType.UpArch ? b.stage - a.stage : a.stage - b.stage,
            ),
        }
      })
    })
    setToothIPRData({
      ...toothIPRData,
      ...newData,
    })
    setEditStageItem({ type: null, editIndex: -1 })
  }

  /**
   * Is Show Add Stage Button
   */
  const isShowUpAddStageButton = useMemo(() => {
    const upData = [
      ...toothIPRData.iprULToothDataMemo,
      ...toothIPRData.iprURToothDataMemo,
    ]
    if (!upData.length) return false
    const isStage = upData[0].data.every((item) => {
      return item.stage !== null
    })

    return isStage && switchViewEdit
  }, [
    toothIPRData.iprULToothDataMemo,
    toothIPRData.iprURToothDataMemo,
    switchViewEdit,
  ])
  const isShowLowAddStageButton = useMemo(() => {
    const lowData = [
      ...toothIPRData.iprLRToothDataMemo,
      ...toothIPRData.iprLLToothDataMemo,
    ]
    if (!lowData.length) return false
    const isStage = lowData[0].data.every((item) => {
      return item.stage !== null
    })

    return isStage && switchViewEdit
  }, [
    toothIPRData.iprLRToothDataMemo,
    toothIPRData.iprLLToothDataMemo,
    switchViewEdit,
  ])

  /**
   * Watch Switch View Edit
   */
  useEffect(() => {
    if (!switchViewEdit) {
      setEditStageItem({ type: null, editIndex: -1 })
      setDeleteStageItem({ type: null, editIndex: -1 })
    }
  }, [switchViewEdit])

  /**
   * Is Have Plan Value
   */
  const isHavePlanValue = (
    type: EArchType,
    stageIndex: number | string | null,
  ) => {
    if (stageIndex === null) return false
    const list =
      type === EArchType.UpArch
        ? [
            ...toothIPRData.iprURToothDataMemo,
            ...toothIPRData.iprULToothDataMemo,
          ]
        : [
            ...toothIPRData.iprLRToothDataMemo,
            ...toothIPRData.iprLLToothDataMemo,
          ]

    const planData = list.filter((item) => {
      return item.data.some((j) => {
        return j.stage === stageIndex && j.value !== ""
      })
    })
    return planData.length > 0
  }

  /**
   * Add Stage
   */
  const handleAddStage = (type: EArchType) => {
    const newItemStage = {
      stage: null,
      value: "",
      actualIPRValue: "",
      isActive: 0,
    }
    const types =
      type === EArchType.UpArch
        ? ["iprURToothDataMemo", "iprULToothDataMemo"]
        : ["iprLRToothDataMemo", "iprLLToothDataMemo"]
    const newData = {}

    types.forEach((item) => {
      newData[item] = toothIPRData[item].map((i) => {
        return {
          ...i,
          data:
            type === EArchType.UpArch
              ? [newItemStage, ...i.data]
              : [...i.data, newItemStage],
        }
      })
    })
    setToothIPRData({
      ...toothIPRData,
      ...newData,
    })
  }

  /**
   * Delete Stage
   */
  const deleteStage = () => {
    const newData = {}
    const { type, editIndex: stageIndex } = deleteStageItem
    const types =
      type === EArchType.UpArch
        ? ["iprURToothDataMemo", "iprULToothDataMemo"]
        : ["iprLRToothDataMemo", "iprLLToothDataMemo"]

    iprModule.clearActualIPRValueByStepIndex(
      toothIPRData[types[0]][0].data[stageIndex].stage,
    )

    types.forEach((item) => {
      newData[item] = toothIPRData[item].map((i) => {
        return {
          ...i,
          data: i.data
            .filter((j, dIndex) => {
              return dIndex !== stageIndex
            })
            .sort((a, b) =>
              type === EArchType.UpArch ? b.stage - a.stage : a.stage - b.stage,
            ),
        }
      })
    })
    setToothIPRData({
      ...toothIPRData,
      ...newData,
    })

    setIsDeleteStage(false)
    setDeleteStageItem({
      type: null,
      editIndex: -1,
    })
  }

  /**
   * Edit Stage
   */
  const editStage = (type: EArchType, stageIndex: number) => {
    if (editStageItem.type === type && editStageItem.editIndex === stageIndex) {
      setEditStageItem({ type: null, editIndex: -1 })
      return
    }

    setEditStageItem({
      type,
      editIndex: stageIndex,
    })
  }

  useImperativeHandle(onRef, () => {
    // 需要将暴露的接口返回出去
    return {
      onSubmit: () => {
        return onSubmit()
      },
    }
  })
  return (
    <>
      {Object.values(toothIPRData)[0].length > 0 ? (
        <div
          className="ipr-report"
          style={{ userSelect: "none" }}
          onMouseDown={(e) => e.stopPropagation()}
          onDragStart={(e) => e.preventDefault()}
        >
          <div className="ipr-report-tooltip">
            IPR should be scheduled before indicated stage starts.
          </div>

          {isPDF && (
            <div className="pdf-desc">
              Before performing Interproximal Reduction (IPR), the clinician
              must verify that the Patient Name and Case Number corresponds to
              the correct patient. ulab Systems, Inc, assumes no responsibility
              or liability for any damages or claims arising from the
              clinician's administration of IPR.
            </div>
          )}

          {/*Total IPR*/}
          {(toothIPRData.iprURToothDataMemo[0].data.length > 0 ||
            toothIPRData.iprLRToothDataMemo[0].data.length > 0) && (
            <div className="ipr-report-header">
              <div className="ipr-report-header-total-ipr">
                <div className="total-text">Total IPR: </div>
                <div className="total-info">
                  <div className="d-flex gap-1">
                    <span>Upper: </span>
                    <span>{upperStageValue.plannedValues}mm(planned)</span>
                    <span>
                      {upperStageValue.actuallyPlacedValues}mm(actually placed)
                    </span>
                  </div>
                  <div className="d-flex gap-1">
                    <span>Lower:</span>
                    <span>{lowerStageValue.plannedValues}mm(planned)</span>
                    <span>
                      {lowerStageValue.actuallyPlacedValues}mm(actually placed)
                    </span>
                  </div>
                </div>
              </div>
              <div className="ipr-report-header-desc">
                <div className="common-data-item">
                  <div className="common-data-num d-flex">0.X</div>
                  <div className="common-data-line" />
                  <div className="common-data-desc">
                    Planned IPR(Check to mark as completed)
                  </div>
                </div>
                <div className="common-data-item">
                  <div className="common-data-num d-flex">0.X</div>
                  <div className="common-data-line" />
                  <div className="common-data-desc">
                    Input and log actually applied IPR
                  </div>
                </div>
              </div>
            </div>
          )}

          <div
            className={classNames("", {
              "scroll-content": !isPDF,
              "hide-scrollbar": isPDF,
            })}
          >
            {/*Add Stage*/}
            {isShowUpAddStageButton && (
              <div className="up-add-stage">
                <Tooltip title="Add Up Arch Stage" placement="top">
                  <AddIcon
                    sx={{ fontSize: 28 }}
                    className="hover"
                    onClick={() => handleAddStage(EArchType.UpArch)}
                  />
                </Tooltip>
              </div>
            )}

            {/*Up Stage */}
            {toothIPRData.iprURToothDataMemo[0].data.length > 0 && (
              <div className="up-stage-wrap">
                {toothIPRData.iprURToothDataMemo[0].data.map(
                  (wrapItem, stageIndex) => {
                    return (
                      <div
                        className="d-flex up-stage-item"
                        key={`${wrapItem.stage}`}
                      >
                        <div className="d-flex up-stage-item-left">
                          {wrapItem.stage !== null &&
                            !isHavePlanValue(
                              EArchType.UpArch,
                              wrapItem.stage,
                            ) &&
                            switchViewEdit && (
                              <div className="d-flex" style={{ gap: "2px" }}>
                                <Tooltip title="Delete" placement="top">
                                  <div
                                    className="d-flex delete-icon-box hover"
                                    onClick={() => {
                                      setDeleteStageItem({
                                        type: EArchType.UpArch,
                                        editIndex: stageIndex,
                                      })
                                      setIsDeleteStage(true)
                                    }}
                                  >
                                    <DeleteForeverIcon
                                      sx={{ fontSize: "16px", color: "#fff" }}
                                    />
                                  </div>
                                </Tooltip>
                                <Tooltip title="Edit" placement="top">
                                  <div
                                    className="d-flex edit-icon-box hover"
                                    style={{
                                      background:
                                        editStageItem.type ===
                                          EArchType.UpArch &&
                                        editStageItem.editIndex === stageIndex
                                          ? "#0288d1"
                                          : "#ccc",
                                    }}
                                    onClick={() =>
                                      editStage(EArchType.UpArch, stageIndex)
                                    }
                                  >
                                    <EditIcon
                                      sx={{
                                        fontSize: "14px",
                                        color: "#fff",
                                      }}
                                    />
                                  </div>
                                </Tooltip>
                              </div>
                            )}

                          <div className="d-flex up-stage-item-num">
                            <span>Stage</span>
                            {wrapItem.stage === null ||
                            (editStageItem.type === EArchType.UpArch &&
                              editStageItem.editIndex === stageIndex) ? (
                              <MyStageInput
                                disabled={!switchViewEdit}
                                inputValue={wrapItem.stage}
                                updateStageData={(val) =>
                                  updateStageData(
                                    val,
                                    EArchType.UpArch,
                                    stageIndex,
                                  )
                                }
                              />
                            ) : (
                              <span className="stage-num">
                                {" "}
                                {wrapItem.stage}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="d-flex common-stage-polyline-wrap">
                          {toothIPRData.iprURToothDataMemo.map(
                            (item, index) => {
                              const hasNonMissingAfter =
                                hasNonMissingToothAfter(
                                  [
                                    ...toothIPRData.iprURToothDataMemo,
                                    ...toothIPRData.iprULToothDataMemo,
                                  ],
                                  index,
                                )
                              return (
                                <div
                                  key={item.id}
                                  className={classNames(
                                    "d-flex stage-polyline",
                                    {
                                      "diabled-missing-tooth":
                                        item.isMissingTooth ||
                                        !hasNonMissingAfter,
                                    },
                                  )}
                                >
                                  <StageItemValue
                                    switchViewEdit={switchViewEdit}
                                    item={item.data[stageIndex]}
                                    dataType="iprURToothDataMemo"
                                    id={item.id}
                                    itemIndex={stageIndex}
                                    updateToothData={updateToothData}
                                    isPDF={isPDF}
                                  />
                                </div>
                              )
                            },
                          )}
                          {toothIPRData.iprULToothDataMemo.map(
                            (item, index) => {
                              const hasNonMissingAfter =
                                hasNonMissingToothAfter(
                                  toothIPRData.iprULToothDataMemo,
                                  index,
                                )
                              return (
                                <div
                                  key={item.id}
                                  className={classNames(
                                    "d-flex stage-polyline",
                                    {
                                      "diabled-missing-tooth":
                                        item.isMissingTooth ||
                                        item.id === EIPRToothMap.UL_8 ||
                                        !hasNonMissingAfter,
                                    },
                                  )}
                                >
                                  <StageItemValue
                                    switchViewEdit={switchViewEdit}
                                    item={item.data[stageIndex]}
                                    dataType="iprULToothDataMemo"
                                    id={item.id}
                                    itemIndex={stageIndex}
                                    updateToothData={updateToothData}
                                    isPDF={isPDF}
                                  />
                                </div>
                              )
                            },
                          )}
                        </div>
                      </div>
                    )
                  },
                )}
              </div>
            )}

            {/* IPR Content 没有16、32  24 特殊处理*/}

            <div
              className={classNames("ipr-reports-view", {
                "have-stage-style":
                  toothIPRData.iprURToothDataMemo[0].data.length ||
                  toothIPRData.iprLRToothDataMemo[0].data.length,
              })}
            >
              <div className="common-tooth-group up-tooth-group">
                {toothIPRData.iprURToothDataMemo[0].data.length > 0 && (
                  <MyUpPolyline toothIPRData={toothIPRData} />
                )}

                <div className="up-right-tooth-group">
                  {toothIPRData.iprURToothDataMemo.map((item, index) => (
                    <div
                      key={item.id}
                      className={classNames("common-tooth-items", {
                        "missing-tooth": item.isMissingTooth,
                        "hide-tooth": index === 0,
                      })}
                    >
                      {item.icon}
                      <div className="up-tooth-id">{item.id}</div>
                    </div>
                  ))}
                </div>
                <div className="up-left-tooth-group">
                  {toothIPRData.iprULToothDataMemo.map((item, index) => (
                    <div
                      key={item.id}
                      className={classNames("common-tooth-items", {
                        "missing-tooth": item.isMissingTooth,
                        "hide-tooth":
                          index === toothIPRData.iprULToothDataMemo.length - 1,
                      })}
                    >
                      {item.icon}
                      <div className="up-tooth-id">{item.id}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="tooth-group-lable">
                <span>R</span>
                <span>L</span>
              </div>

              <div className="common-tooth-group  low-tooth-group">
                {toothIPRData.iprLRToothDataMemo[0].data.length > 0 && (
                  <MyLowPolyline toothIPRData={toothIPRData} />
                )}
                <div className="low-left-tooth-group">
                  {toothIPRData.iprLRToothDataMemo.map((item, index) => (
                    <div
                      key={item.id}
                      className={classNames("common-tooth-items", {
                        "missing-tooth": item.isMissingTooth,
                        "hide-tooth": index === 0,
                      })}
                    >
                      {item.icon}
                      {/* <div className="low-tooth-id">{item.id}</div> */}
                    </div>
                  ))}
                </div>
                <div className="low-right-tooth-group">
                  {toothIPRData.iprLLToothDataMemo.map((item, index) => (
                    <div
                      key={item.id}
                      className={classNames("common-tooth-items", {
                        "missing-tooth": item.isMissingTooth,
                        "hide-tooth":
                          index === toothIPRData.iprLLToothDataMemo.length - 1,
                      })}
                    >
                      {item.icon}
                      {/* <div className="low-tooth-id">{item.id}</div> */}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/*Low Stage*/}
            {toothIPRData.iprLRToothDataMemo[0].data.length > 0 && (
              <div className="low-stage-wrap">
                {toothIPRData.iprLRToothDataMemo[0].data.map(
                  (stageItem, stageIndex) => {
                    return (
                      <div
                        className="d-flex low-stage-item"
                        key={`low-${stageIndex}`}
                      >
                        <div className="d-flex low-stage-item-left">
                          {stageItem.stage !== null &&
                            !isHavePlanValue(
                              EArchType.LowArch,
                              stageItem.stage,
                            ) &&
                            switchViewEdit && (
                              <div className="d-flex" style={{ gap: "2px" }}>
                                <Tooltip title="Delete" placement="top">
                                  <div
                                    className="d-flex delete-icon-box hover"
                                    onClick={() => {
                                      setDeleteStageItem({
                                        type: EArchType.LowArch,
                                        editIndex: stageIndex,
                                      })
                                      setIsDeleteStage(true)
                                    }}
                                  >
                                    <DeleteForeverIcon
                                      sx={{ fontSize: "16px", color: "#fff" }}
                                    />
                                  </div>
                                </Tooltip>
                                <Tooltip title="Edit" placement="top">
                                  <div
                                    className="d-flex edit-icon-box hover"
                                    style={{
                                      background:
                                        editStageItem.type ===
                                          EArchType.LowArch &&
                                        editStageItem.editIndex === stageIndex
                                          ? "#0288d1"
                                          : "#ccc",
                                    }}
                                    onClick={() =>
                                      editStage(EArchType.LowArch, stageIndex)
                                    }
                                  >
                                    <EditIcon
                                      sx={{
                                        fontSize: "14px",
                                        color: "#fff",
                                      }}
                                    />
                                  </div>
                                </Tooltip>
                              </div>
                            )}

                          <div className="d-flex low-stage-item-num">
                            <span>Stage</span>
                            {stageItem.stage === null ||
                            (editStageItem.type === EArchType.LowArch &&
                              editStageItem.editIndex === stageIndex) ? (
                              <MyStageInput
                                disabled={!switchViewEdit}
                                inputValue={stageItem.stage}
                                updateStageData={(val) =>
                                  updateStageData(
                                    val,
                                    EArchType.LowArch,
                                    stageIndex,
                                  )
                                }
                              />
                            ) : (
                              <span className="stage-num">
                                {" "}
                                {stageItem.stage}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="d-flex common-stage-polyline-wrap">
                          {toothIPRData.iprLRToothDataMemo.map(
                            (item, index) => {
                              const hasNonMissingBefore =
                                hasNonMissingToothBefore(
                                  toothIPRData.iprLRToothDataMemo,
                                  index,
                                )
                              return (
                                // <>
                                //   {item.isMissingTooth ||
                                //   (toothIPRData.iprLRToothDataMemo[0]
                                //     .isMissingTooth &&
                                //     index === 1) ||
                                //   index === 0 ||
                                //   !hasNonMissingBefore ? (
                                //     <div>----</div>
                                //   ) : (
                                //     <div
                                //       key={item.id}
                                //       className={classNames(
                                //         "d-flex stage-polyline",
                                //         // {
                                //         //   "diabled-missing-tooth":
                                //         //     item.isMissingTooth ||
                                //         //     (toothIPRData.iprLRToothDataMemo[0]
                                //         //       .isMissingTooth &&
                                //         //       index === 1) ||
                                //         //     index === 0 ||
                                //         //     !hasNonMissingBefore,
                                //         // },
                                //       )}
                                //     >
                                //       <StageItemValue
                                //         switchViewEdit={switchViewEdit}
                                //         item={item.data[stageIndex]}
                                //         dataType="iprLRToothDataMemo"
                                //         id={item.id}
                                //         itemIndex={stageIndex}
                                //         updateToothData={updateToothData}
                                //         isPDF={isPDF}
                                //       />
                                //     </div>
                                //   )}
                                // </>
                                <div
                                  key={item.id}
                                  className={classNames(
                                    "d-flex stage-polyline",
                                    {
                                      "diabled-missing-tooth":
                                        item.isMissingTooth ||
                                        (toothIPRData.iprLRToothDataMemo[0]
                                          .isMissingTooth &&
                                          index === 1) ||
                                        index === 0 ||
                                        !hasNonMissingBefore,
                                    },
                                  )}
                                >
                                  <StageItemValue
                                    switchViewEdit={switchViewEdit}
                                    item={item.data[stageIndex]}
                                    dataType="iprLRToothDataMemo"
                                    id={item.id}
                                    itemIndex={stageIndex}
                                    updateToothData={updateToothData}
                                    isPDF={isPDF}
                                  />
                                </div>
                              )
                            },
                          )}
                          {toothIPRData.iprLLToothDataMemo.map(
                            (item, index) => {
                              const hasNonMissingAfter =
                                hasNonMissingToothAfter(
                                  [
                                    ...toothIPRData.iprLLToothDataMemo,
                                    ...toothIPRData.iprLRToothDataMemo,
                                  ],
                                  index,
                                )

                              return (
                                <div
                                  key={item.id}
                                  className={classNames(
                                    "d-flex stage-polyline",
                                    {
                                      "diabled-missing-tooth":
                                        item.isMissingTooth ||
                                        !hasNonMissingAfter,
                                    },
                                  )}
                                >
                                  <StageItemValue
                                    switchViewEdit={switchViewEdit}
                                    item={item.data[stageIndex]}
                                    dataType="iprLLToothDataMemo"
                                    id={item.id}
                                    itemIndex={stageIndex}
                                    updateToothData={updateToothData}
                                    isPDF={isPDF}
                                  />
                                </div>
                              )
                            },
                          )}
                        </div>
                      </div>
                    )
                  },
                )}
              </div>
            )}
            {/*Add Stage*/}
            {isShowLowAddStageButton && (
              <div className="low-add-stage">
                <Tooltip title="Add Low Arch Stage" placement="top">
                  <AddIcon
                    sx={{ fontSize: 28 }}
                    className="hover"
                    onClick={() => handleAddStage(EArchType.LowArch)}
                  />
                </Tooltip>
              </div>
            )}

            {isPDF ? (
              <>
                {notes && (
                  <div className="add-notes">
                    <div className="add-notes-text">
                      Use the text box to notate any additional detail,
                      clarifications, or clinical observations.
                    </div>
                    <div className="add-note-input my-box-style">{notes}</div>
                  </div>
                )}
              </>
            ) : (
              <div className="add-notes">
                <div className="add-notes-text">
                  Use the text box to notate any additional detail,
                  clarifications, or clinical observations.
                </div>
                <TextField
                  disabled={!switchViewEdit}
                  inputRef={myTextAreaRef}
                  id="outlined-multiline-flexible"
                  className="add-note-input"
                  placeholder="Enter notes here..."
                  label=""
                  multiline
                  rows={4}
                  value={notesValue}
                  onChange={(e) => {
                    setNotesValue(e.target.value)
                  }}
                  onKeyDown={onKeyDown}
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="ipr-report-loading">
          <CircularProgress
            variant="indeterminate"
            disableShrink
            sx={{
              color: (theme) =>
                theme.palette.mode === "light" ? "#1a90ff" : "#308fe8",
              animationDuration: "550ms",
            }}
            size={40}
            thickness={4}
          />
        </div>
      )}

      {/* Message */}
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={3000}
        open={!!message}
        onClose={() => {
          setMessage("")
        }}
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={() => setMessage("")}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      >
        <Alert severity="info">{message}</Alert>
      </Snackbar>

      {/*Delete Stage Model*/}
      <MyDialog
        open={isDeleteStage}
        title="Warning"
        content="Are you sure you want to remove this data from this page?"
        actions={
          <div className="d-flex gap-1">
            <Button
              className="btn btn-danger"
              variant="contained"
              onClick={deleteStage}
            >
              Confirm
            </Button>
            <Button
              className="btn btn-outline-primary"
              variant="outlined"
              onClick={() => {
                setIsDeleteStage(false)
                setDeleteStageItem({
                  type: null,
                  editIndex: -1,
                })
              }}
            >
              Cancel
            </Button>
          </div>
        }
      />
    </>
  )
}

export default IPRReport
